import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { environment } from '@environment';
import { BridgeService } from '../services/bridge.service';

interface QueryParamsLike {
  keys(): string[];
  get(k: string): string | null;
}

function shouldRoute(url: string): boolean {
  if (!environment.bridgeUrl || isLocalHost()) return false;
  try {
    const target = new URL(url, window.location.origin);
    const backend = new URL(environment.backendUrl, window.location.origin);
    if (target.origin !== backend.origin) return false;
    return target.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function isLaunchRequest(url: string, params: QueryParamsLike): boolean {
  try {
    const target = new URL(url, window.location.origin);
    const backend = new URL(environment.backendUrl, window.location.origin);
    const sameEndpoint =
      target.origin === backend.origin &&
      normalizePath(target.pathname) === normalizePath(backend.pathname);

    if (!sameEndpoint) return false;

    return target.searchParams.has('gameId') || params.keys().includes('gameId');
  } catch {
    return false;
  }
}

export const bridgeInterceptor: HttpInterceptorFn = (req, next) => {
  if (!shouldRoute(req.url) || isLaunchRequest(req.url, req.params)) {
    return next(req);
  }

  const bridge = inject(BridgeService);

  const headers: Record<string, string> = {};
  req.headers.keys().forEach((k) => {
    const v = req.headers.get(k);
    if (v != null) headers[k] = v;
  });
  if (!headers['accept']) headers['accept'] = 'application/json, text/plain, */*';

  const fullUrl = buildUrl(req.url, req.params);

  return from(
    bridge.request(req.method, fullUrl, headers, req.body),
  ).pipe(
    map((res) => {
      if (!res.ok) {
        throw new HttpErrorResponse({
          url: fullUrl,
          status: res.status,
          statusText: res.statusText,
          error: res.data ?? res.error,
        });
      }
      return new HttpResponse({
        url: fullUrl,
        status: res.status,
        statusText: res.statusText,
        body: res.data,
        headers: new HttpHeaders(),
      });
    }),
  ) as Observable<never>;
};

function buildUrl(url: string, params: QueryParamsLike): string {
  const keys = params.keys();
  if (!keys.length) return url;
  const sep = url.includes('?') ? '&' : '?';
  const qs = keys
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params.get(k) ?? '')}`)
    .join('&');
  return `${url}${sep}${qs}`;
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '');
}

function isLocalHost(): boolean {
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}
